import React, { useMemo, useState } from "react";

// Formatos
function fmtUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n) || 0);
}

function pctSafe(num, den) {
  if (!den) return '0.00';
  const v = ((Number(num) || 0) / den) * 100;
  return isNaN(v) ? '0.00' : v.toFixed(2);
}

function subsidyText(num, den) {
  var p = pctSafe(num, den);
  return p === '0.00' ? '0.00%' : '- ' + p + '%';
}

function subsidyCls(num, den) {
  var p = pctSafe(num, den);
  return 'text-center' + (p !== '0.00' ? ' text-danger' : '');
}

// Helper: primer valor no null/undefined
function pick() {
  for (var i = 0; i < arguments.length; i++) {
    var v = arguments[i];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

// Agrupa filas planas -> { state -> { totals, categories -> { totals, clients{} } } }
function buildHierarchy(rows) {
  var out = {};
  rows = Array.isArray(rows) ? rows : [];
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i] || {};

    var state = r.ShipToState ? String(r.ShipToState) : (r.state ? String(r.state) : "NA");
    var lob = r.ProductLineDesc ? String(r.ProductLineDesc) : (r.lob ? String(r.lob) : "SIN_CATEGORIA");
    var client = r.BillToName ? String(r.BillToName) : (r.CustomerName ? String(r.CustomerName) : (r.client ? String(r.client) : "Unknown"));

    var envios = Number(pick(r.Envios, r.envios, r.count, 0) || 0);
    var freight = Number(pick(r.FreightAmt, r.freight, r.FletesCarrier, 0) || 0);
    var payed = Number(pick(r.Payed, r.payed, 0) || 0);
    var diffPct = Number(pick(r.diffPct, r.subsidioPct, r.pct, 0) || 0);
    var subsidyDiff = payed && freight ? payed - freight : 0;

    if (!out[state]) {
      out[state] = { totals: { envios: 0, freight: 0, payed: 0, subsidyDiff: 0, diffAgg: 0, diffCount: 0 }, categories: {} };
    }
    if (!out[state].categories[lob]) {
      out[state].categories[lob] = { totals: { envios: 0, freight: 0, payed: 0, subsidyDiff: 0, diffAgg: 0, diffCount: 0 }, clients: {} };
    }
    if (!out[state].categories[lob].clients[client]) {
      out[state].categories[lob].clients[client] = { envios: 0, freight: 0, payed: 0, subsidyDiff: 0, diffAgg: 0, diffCount: 0 };
    }

    // Cliente
    var c = out[state].categories[lob].clients[client];
    c.envios += envios;
    c.freight += freight;
    c.payed += payed;
    c.subsidyDiff += subsidyDiff;
    c.diffAgg += diffPct;
    c.diffCount += 1;

    // Categoría
    var cat = out[state].categories[lob].totals;
    cat.envios += envios;
    cat.freight += freight;
    cat.payed += payed;
    cat.subsidyDiff += subsidyDiff;
    cat.diffAgg += diffPct;
    cat.diffCount += 1;

    // Estado
    var st = out[state].totals;
    st.envios += envios;
    st.freight += freight;
    st.payed += payed;
    st.subsidyDiff += subsidyDiff;
    st.diffAgg += diffPct;
    st.diffCount += 1;
  }

  // A arreglos ordenados
  var states = Object.keys(out).sort();
  var result = states.map(function (stKey) {
    var st = out[stKey];
    var catsKeys = Object.keys(st.categories).sort();
    var cats = catsKeys.map(function (cKey) {
      var cat = st.categories[cKey];
      var cliKeys = Object.keys(cat.clients).sort();
      var clis = cliKeys.map(function (cliKey) {
        var v = cat.clients[cliKey];
        return {
          client: cliKey,
          envios: v.envios,
          freight: v.freight,
          payed: v.payed,
          subsidyDiff: v.subsidyDiff,
          diffPct: v.diffCount ? v.diffAgg / v.diffCount : 0,
        };
      });
      return {
        lob: cKey,
        envios: cat.totals.envios,
        freight: cat.totals.freight,
        payed: cat.totals.payed,
        subsidyDiff: cat.totals.subsidyDiff,
        diffPct: cat.totals.diffCount ? cat.totals.diffAgg / cat.totals.diffCount : 0,
        clients: clis,
      };
    });
    return {
      state: stKey,
      envios: st.totals.envios,
      freight: st.totals.freight,
      payed: st.totals.payed,
      subsidyDiff: st.totals.subsidyDiff,
      diffPct: st.totals.diffCount ? st.totals.diffAgg / st.totals.diffCount : 0,
      categories: cats,
    };
  });

  // Totales tfoot
  var total = result.reduce(function (acc, s) {
    acc.envios += s.envios;
    acc.freight += s.freight;
    acc.payed += s.payed;
    acc.subsidyDiff += s.subsidyDiff;
    acc.diffAgg += s.diffPct;
    acc.diffCount += 1;
    return acc;
  }, { envios: 0, freight: 0, payed: 0, subsidyDiff: 0, diffAgg: 0, diffCount: 0 });

  var totalRow = {
    envios: total.envios,
    freight: total.freight,
    payed: total.payed,
    subsidyDiff: total.subsidyDiff,
    diffPct: total.diffCount ? total.diffAgg / total.diffCount : 0,
  };

  return { list: result, total: totalRow };
}

export default function ShipToStateTable(props) {
  var rows = props && props.rows ? props.rows : [];
  // var onExport = props && props.onExport ? props.onExport : function () {};
  var _useState = useState({}), openStates = _useState[0], setOpenStates = _useState[1];
  var _useState2 = useState({}), openCats = _useState2[0], setOpenCats = _useState2[1];

  var data = useMemo(function () { return buildHierarchy(rows); }, [rows]);

  function toggleState(st) {
    var next = {};
    for (var k in openStates) { if (Object.prototype.hasOwnProperty.call(openStates, k)) next[k] = openStates[k]; }
    next[st] = !next[st];
    setOpenStates(next);
  }
  function toggleCat(st, lob) {
    var key = st + "|" + lob;
    var next = {};
    for (var k in openCats) { if (Object.prototype.hasOwnProperty.call(openCats, k)) next[k] = openCats[k]; }
    next[key] = !next[key];
    setOpenCats(next);
  }

  return (
    <div className="card glass-card table-card w-100 bg-light">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold darkerPurpleText">By state</h6>
        {/* <button className="btn btn-sm bg-light text-primary" onClick={onExport}>Export CSV</button> */}
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>State</th>
              <th className="text-center">Total</th>
              <th className="text-center">Fletes Carrier</th>
              <th className="text-center">Envios</th>
              <th className="text-center">Subsidio</th>
            </tr>
          </thead>
          <tbody>
            {data.list.map(function (s) {
              var stOpen = !!openStates[s.state];
              return (
                <React.Fragment key={s.state}>
                  {/* Estado */}
                  <tr className="state-row" onClick={function () { return toggleState(s.state); }} role="button">
                    <td className="fw-semibold">
                      <span className={"chev " + (stOpen ? "rotate" : "")}>▶</span>
                      <span className="ms-2">{s.state}</span>
                    </td>
                    <td className="text-center">{s.envios}</td>
                    <td className="text-center">{fmtUSD(s.payed)}</td>
                    <td className="text-center">{((s.envios * 100) / data.list.length).toFixed(0)}%</td>
                    <td className={subsidyCls(s.subsidyDiff, s.payed)}>{subsidyText(s.subsidyDiff, s.payed)}</td>
                  </tr>

                  {/* Categorías */}
                  {stOpen && s.categories.map(function (c) {
                    var key = s.state + "|" + c.lob;
                    var catOpen = !!openCats[key];

                    if (c.lob === 'N/A') {
                      return c.clients
                        .filter(function(cli) { return cli.client !== 'N/A' && cli.client !== 'Unknown'; })
                        .map(function(cli) {
                          return (
                            <tr className="client-row" key={s.state + "|N/A|" + cli.client}>
                              <td className="indent-1">{cli.client}</td>
                              <td className="text-center">{cli.envios}</td>
                              <td className="text-center">{fmtUSD(cli.payed)}</td>
                              <td className="text-center"></td>
                              <td className={subsidyCls(cli.subsidyDiff, cli.payed)}>{subsidyText(cli.subsidyDiff, cli.payed)}</td>
                            </tr>
                          );
                        });
                    }

                    return (
                      <React.Fragment key={key}>
                        <tr className="cat-row" onClick={function () { return toggleCat(s.state, c.lob); }} role="button">
                          <td className="indent-1">
                            <span className={"chev " + (catOpen ? "rotate" : "")}>▶</span>
                            <span className="ms-2">{c.lob}</span>
                          </td>
                          <td className="text-center">{c.envios}</td>
                          <td className="text-center">{fmtUSD(c.payed)}</td>
                          <td className="text-center"></td>
                          <td className={subsidyCls(c.subsidyDiff, c.payed)}>{subsidyText(c.subsidyDiff, c.payed)}</td>
                        </tr>

                        {/* Clientes */}
                        {catOpen && c.clients.filter(function(cli) { return cli.client !== 'N/A' && cli.client !== 'Unknown'; }).map(function (cli) {
                          return (
                            <tr className="client-row" key={s.state + "|" + c.lob + "|" + cli.client}>
                              <td className="indent-2">{cli.client}</td>
                              <td className="text-center">{cli.envios}</td>
                              <td className="text-center">{fmtUSD(cli.payed)}</td>
                              <td className="text-center"></td>
                              <td className={subsidyCls(cli.subsidyDiff, cli.payed)}>{subsidyText(cli.subsidyDiff, cli.payed)}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="table-total">
              <td>Total</td>
              <td className="text-center">{data.total.envios}</td>
              <td className="text-center">{fmtUSD(data.total.payed)}</td>
              <td className="text-center">100%</td>
              <td className={subsidyCls(data.total.subsidyDiff, data.total.payed)}>{subsidyText(data.total.subsidyDiff, data.total.payed)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
